import { useState } from "react";
import type { NetworkTraceResult, TraceDirection } from "../model/inp/networkTrace.ts";

export type { TraceDirection } from "../model/inp/networkTrace.ts";

interface NetworkTraceState extends NetworkTraceResult {
  direction: TraceDirection;
  startNode: string;
}

/**
 * "Trace upstream/downstream from a node" — the result is shown as a
 * highlight directly on the map plus NetworkTraceBar.tsx, not a dialog.
 * Two entry points:
 *  - `runFrom(startNode, direction, trace)`: the contextual-action path
 *    (audit finding: the trace tools were moved from the ToolRail's own
 *    Tools menu to the PropertyPanel of a selected node, where the start
 *    node is already known). Skips the pick round trip below entirely.
 *  - `startPick(direction)` + `resolvePick(startNode, trace)`: the
 *    pick-a-node-on-the-map round trip, same shape as
 *    useLongitudinalProfile.ts's own `picking`/`resolvePick`/`cancelPick`.
 *    Triggered from ToolRail.tsx's own "Herramientas" menu (ToolsMenu.tsx)
 *    — brought back deliberately as a standing home for future tools,
 *    alongside the contextual shortcut above, not instead of it.
 */
export function useNetworkTrace() {
  const [picking, setPicking] = useState<TraceDirection | null>(null);
  const [result, setResult] = useState<NetworkTraceState | null>(null);

  function startPick(direction: TraceDirection) {
    setResult(null);
    setPicking(direction);
  }

  /** Resolves a node click against `trace` (App.tsx owns the actual `traceNetwork` call, since it needs `project`). Returns false if not currently picking, so the caller can fall through to normal click handling. */
  function resolvePick(startNode: string, trace: NetworkTraceResult): boolean {
    if (!picking) return false;
    setResult({ direction: picking, startNode, ...trace });
    setPicking(null);
    return true;
  }

  function cancelPick() {
    setPicking(null);
  }

  /** Direct-run variant: caller already knows the start node (e.g. it's the currently-selected one in PropertyPanel) so we skip pick mode entirely and just store the freshly-computed trace. */
  function runFrom(startNode: string, direction: TraceDirection, trace: NetworkTraceResult) {
    setPicking(null);
    setResult({ direction, startNode, ...trace });
  }

  function clear() {
    setPicking(null);
    setResult(null);
  }

  /** Same as `clear`, but named for the project-switch case: a trace over one project's node/link names has no meaning in another. */
  function reset() {
    setPicking(null);
    setResult(null);
  }

  return { picking, result, startPick, resolvePick, runFrom, cancelPick, clear, reset };
}
